import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getSessionByToken } from "../../services/publicSessionLinkService";
import RegistrationForm from "./RegistrationForm";
import OtpScreen from "./OtpScreen";
import { PLAN_TYPE_META } from "../../../common/constants";
import "./sessionLanding.css";

const SessionLandingPage = () => {
  const { token } = useParams();

  const [session, setSession] = useState(null);
  const [pageState, setPageState] = useState("loading");
  const [otpData, setOtpData] = useState(null);   // { email, userName, message }

  useEffect(() => {
    const load = async () => {
      const result = await getSessionByToken(token);
      if (!result.data || result.status === 404 || result.status === 500) { setPageState("error"); return; }
      const s = result.data;
      setSession(s);
      if (s.isCancelled) { setPageState("cancelled"); return; }
      if (s.isExpired || new Date(s.endTime) < new Date()) { setPageState("expired"); return; }
      if (s.remainingSlots === 0) { setPageState("full"); return; }
      setPageState("form");
    };
    load();
  }, [token]);

  const formatLocalTime = (iso) =>
    iso ? new Date(iso).toLocaleString("en-US", {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      dateStyle: "full", timeStyle: "short",
    }) : "—";

  const getDuration = (start, end) => {
    if (!start || !end) return "—";
    const mins = Math.round((new Date(end) - new Date(start)) / 60000);
    return mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60 > 0 ? `${mins % 60}m` : ""}`.trim() : `${mins} min`;
  };

  // ── State screens ──
  if (pageState === "loading") return (
    <div className="sl-state-page"><div className="sl-state-card">
      <span className="sl-state-icon">⏳</span><h2>Loading session...</h2>
    </div></div>
  );

  if (pageState === "error") return (
    <div className="sl-state-page"><div className="sl-state-card">
      <span className="sl-state-icon">🔗</span>
      <h2>Link Not Available</h2>
      <p>This link is no longer available or has expired.</p>
    </div></div>
  );

  if (pageState === "cancelled") return (
    <div className="sl-state-page"><div className="sl-state-card">
      <span className="sl-state-icon">❌</span>
      <h2>Session Cancelled</h2>
      <p>This session has been cancelled. Please check back for future sessions.</p>
    </div></div>
  );

  if (pageState === "expired") return (
    <div className="sl-state-page"><div className="sl-state-card">
      <span className="sl-state-icon">⌛</span>
      <h2>Session Ended</h2>
      <p>This session has already ended. Stay tuned for upcoming sessions!</p>
    </div></div>
  );

  if (pageState === "full") return (
    <div className="sl-state-page"><div className="sl-state-card">
      <span className="sl-state-icon">🪑</span>
      <h2>Session is Full</h2>
      <p>All seats for <strong>{session?.topic}</strong> have been taken.</p>
    </div></div>
  );

  if (pageState === "otp") return (
    <OtpScreen
      token={token}
      email={otpData.email}
      userName={otpData.userName}
    />
  );

  if (pageState === "success") return null;

  // ── Main landing page ──
  return (
    <div className="sl-landing">
      <div className="sl-landing-card">
        <div className="sl-session-header">
          <div className="sl-speaker-row">
            {session.speakerAvatarUrl
              ? <img className="sl-speaker-avatar" src={session.speakerAvatarUrl} alt={session.speakerName} />
              : (
                <div className="sl-speaker-avatar-placeholder">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
                    <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
                  </svg>
                </div>
              )}
            <div>
              <div className="sl-speaker-name">{session.speakerName}</div>
              <div className="sl-speaker-label">Session Host</div>
            </div>
          </div>

          <h1 className="sl-session-topic">{session.topic}</h1>

          <div className="sl-session-meta">
            <span className="sl-meta-chip">📚 {session.level}</span>
            <span className="sl-meta-chip">
              {PLAN_TYPE_META[session.planType]?.displayName ?? session.planType}
            </span>

          </div>
        </div>

        <div className="sl-session-details">
          <div className="sl-detail-row">
            <span className="sl-detail-label">📅 Date &amp; Time</span>
            <span className="sl-detail-value sl-detail-value-datetime">{formatLocalTime(session.startTime)}</span>
          </div>
          <div className="sl-detail-row">
            <span className="sl-detail-label">⏱ Duration</span>
            <span className="sl-detail-value">{getDuration(session.startTime, session.endTime)}</span>
          </div>
          <div className="sl-detail-row">
            <span className="sl-detail-label">🎯 Level</span>
            <span className="sl-detail-value">{session.level}</span>
          </div>
          <div className="sl-detail-row">
            <span className="sl-detail-label">📋 Plan Type</span>
            <span className="sl-detail-value">
              <span className="sl-plan-badge">{PLAN_TYPE_META[session.planType]?.displayName ?? session.planType}</span>
            </span>
          </div>
        </div>

        <RegistrationForm
          token={token}
          onOtpRequired={(data) => { setOtpData(data); setPageState("otp"); }}
        />
      </div>
    </div>
  );
};

export default SessionLandingPage;
